import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle } from 'lucide-react';
import { changePasswordSchema, type ChangePasswordInput } from '@lms/shared';
import { api, setAccessToken, toApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { AuthCard, AuthShell, FieldLabel } from '@/components/auth/AuthCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async (values: ChangePasswordInput) => {
    setServerError(null);
    try {
      await api.post('/auth/change-password', values);
      // The server revoked every session, including this one — send the user
      // back to sign in rather than leaving a dead token in memory.
      setAccessToken(null);
      setUser(null);
      navigate('/login', { replace: true });
    } catch (error) {
      setServerError(toApiError(error).message);
    }
  };

  return (
    <AuthShell>
      <AuthCard
        title={user?.mustChangePassword ? 'Set your password' : 'Change your password'}
        subtitle={
          user?.mustChangePassword
            ? 'Your account was created with a temporary password'
            : 'You will be signed out of all devices'
        }
      >
        {serverError && (
          <div
            role="alert"
            className="mb-5 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-[13px] text-destructive"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{serverError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Field
            id="currentPassword"
            label={user?.mustChangePassword ? 'Temporary password' : 'Current password'}
            autoComplete="current-password"
            error={errors.currentPassword?.message}
            {...register('currentPassword')}
          />
          <Field
            id="newPassword"
            label="New password"
            autoComplete="new-password"
            hint="At least 8 characters, with upper case, lower case and a number."
            error={errors.newPassword?.message}
            {...register('newPassword')}
          />
          <Field
            id="confirmPassword"
            label="Confirm new password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <Button
            type="submit"
            className="mt-1 h-[44px] w-full rounded-xl text-[14px]"
            loading={isSubmitting}
          >
            {isSubmitting ? 'Saving…' : 'Change password'}
          </Button>
        </form>
      </AuthCard>
    </AuthShell>
  );
}

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string | undefined;
  hint?: string;
}

const Field = ({ id, label, error, hint, ...props }: FieldProps) => (
  <div className="space-y-1.5">
    <FieldLabel htmlFor={id}>
      {label}
    </FieldLabel>
    <Input
      id={id}
      type="password"
      className="h-[42px] rounded-xl px-3.5 text-[14px]"
      invalid={Boolean(error)}
      aria-required="true"
      {...props}
    />
    {hint && !error && <p className="text-[12px] text-muted-foreground">{hint}</p>}
    {error && <p className="text-[12.5px] text-destructive">{error}</p>}
  </div>
);
